import {useEffect, useState} from 'react';
import {SearchResults} from 'algoliasearch-helper';

interface IHighlightedField {
  value?: string;
}

interface IHighlightedSearchIndexData {
  title?: IHighlightedField;
  subtitle?: IHighlightedField;
  dateRange?: IHighlightedField;
}

interface ISearchIndexDataSuggestion {
  title?: string;
  subtitle?: string;
  dateRange?: string;
}

export interface ISearchEventSuggestion {
  searchIndexData: ISearchIndexDataSuggestion;
  _highlightResult?: {
    searchIndexData?: IHighlightedSearchIndexData;
  };
  formattedDate: string;
}

const getSearchIndexData = (hit: SearchResults['hits'][number]): ISearchIndexDataSuggestion => {
  if (!hit || typeof hit !== 'object') {
    return {};
  }

  const record = hit as Record<string, unknown>;
  const data = record.searchIndexData;
  if (!data || typeof data !== 'object') {
    return {};
  }

  const searchIndexData = data as Record<string, unknown>;
  return {
    title: typeof searchIndexData.title === 'string' ? searchIndexData.title : undefined,
    subtitle: typeof searchIndexData.subtitle === 'string' ? searchIndexData.subtitle : undefined,
    dateRange: typeof searchIndexData.dateRange === 'string' ? searchIndexData.dateRange : undefined,
  };
};

const getHighlightResult = (
  hit: SearchResults['hits'][number]
): { searchIndexData?: IHighlightedSearchIndexData } | undefined => {
  if (!hit || typeof hit !== 'object') {
    return undefined;
  }

  const record = hit as Record<string, unknown>;
  const highlightResult = record._highlightResult;
  if (!highlightResult || typeof highlightResult !== 'object') {
    return undefined;
  }

  const highlightRecord = highlightResult as Record<string, unknown>;
  const searchIndexDataRaw = highlightRecord.searchIndexData;
  if (!searchIndexDataRaw || typeof searchIndexDataRaw !== 'object') {
    return undefined;
  }

  const searchIndexDataRecord = searchIndexDataRaw as Record<string, unknown>;
  const getField = (fieldName: 'title' | 'subtitle' | 'dateRange'): IHighlightedField | undefined => {
    const field = searchIndexDataRecord[fieldName];
    if (!field || typeof field !== 'object') {
      return undefined;
    }

    const fieldRecord = field as Record<string, unknown>;
    return typeof fieldRecord.value === 'string' ? {value: fieldRecord.value} : undefined;
  };

  return {
    searchIndexData: {
      title: getField('title'),
      subtitle: getField('subtitle'),
      dateRange: getField('dateRange'),
    },
  };
};

export const useSearchEvents = (searchQuery: string): ISearchEventSuggestion[] => {
  const [suggestions, setSuggestions] = useState<SearchResults['hits']>([]);

  useEffect(() => {
    if (!searchQuery) {
      setSuggestions([]);
      return;
    }

    const {algoliasearch, instantsearch} = window;
    const searchClient = algoliasearch('1JVBWNUDCT', 'ee9a1fcf209119ef8c912244515d2300');
    const search = instantsearch({
      indexName: 'onsite-calendar-events',
      searchClient,
      searchFunction(helper) {
        helper.setQuery(searchQuery).search();
      },
      future: {preserveSharedStateOnUnmount: true},
    });

    const customHitsWidget = {
      $$type: 'custom.hitsWidget',
      init() {
      },
      render({results}: { results: SearchResults }) {
        setSuggestions(results.hits);
      },
      dispose() {
        setSuggestions([]);
      },
    };

    search.addWidgets([
      instantsearch.widgets.configure({
        hitsPerPage: 500,
      }),
      customHitsWidget,
    ]);

    search.start();

    return () => {
      search.dispose();
    };
  }, [searchQuery]);

  return suggestions.map((hit) => {
    const searchIndexData = getSearchIndexData(hit);
    const dateRange = searchIndexData.dateRange;
    const parts = dateRange ? dateRange.split('-') : [];
    const startMonthDay = parts[0]?.trim(); // "November 13"
    const endYear = parts[1]?.split(',')[1]?.trim(); // "2024"

    // Combine startMonthDay and endYear to create a valid date string; if it's a single-day value, use dateRange directly.
    const hasValidRangeStartDate = startMonthDay && endYear && !isNaN(Date.parse(`${startMonthDay}, ${endYear}`));
    const formattedDate = hasValidRangeStartDate
      ? new Date(`${startMonthDay}, ${endYear}`).toISOString().split('T')[0]
      : (dateRange && !isNaN(Date.parse(dateRange))
          ? new Date(dateRange).toISOString().split('T')[0]
          : '');

    return {
      searchIndexData,
      _highlightResult: getHighlightResult(hit),
      formattedDate,
    };
  });

};
